import styles from './FlatCard.module.css';
import Icon from '../../Icon';
import CardIconButton from '../../UI/CardIconButton/CardIconButton';
import { useFavorite } from '../../../pages/FavoritePage/FavoriteContext';
import { useCompare } from '../../../pages/ComparePage/CompareContext';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { FLAT_PAGE } from '../../../router/paths';
import { FlatCardData } from '../types';
import { getDisplayPricePerM2 } from '../../../utils/discountPriceUtils';

interface FlatCardProps {
  data: FlatCardData;
  isDiscounted: boolean;
}

export default function Card({ data, isDiscounted }: FlatCardProps) {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isHovered, setIsHovered] = useState(false);

  const { favorites, toggleFavorite } = useFavorite();
  const { compares, toggleCompare } = useCompare();
  const navigate = useNavigate();

  const isFavorite = favorites.some((item) => item.id === data.id && item.type === data.type);
  const isCompared = compares.some((item) => item.id === data.id && item.type === data.type);
  const hasImage = Boolean(data.image);
  const hasSecondImage = Boolean(data.imageSecond) && hasImage;
  const primaryImage = hasImage ? data.image : data.imageSecond;
  const areaValue = Number(String(data.area ?? '').replace(',', '.').replace(/\s/g, ''));
  const isSoldByArea = !isNaN(areaValue) && areaValue >= 117 && areaValue < 119;
  const isSold = !data.available || isSoldByArea;

  const handleToggleFavorite = () => {
    toggleFavorite(data);
  };

  const handleToggleCompare = () => {
    toggleCompare(data);
  };

  const handleMouseMove = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!hasSecondImage) return;
    const { left, width } = event.currentTarget.getBoundingClientRect();
    const mouseX = event.clientX - left;

    if (mouseX < width / 2) {
      setActiveImageIndex(0);
    } else {
      setActiveImageIndex(1);
    }
  };

  const handleMouseEnter = () => {
    if (hasSecondImage) {
      setIsHovered(true);
    }
  };

  const handleMouseLeave = () => {
    setIsHovered(false);
    setActiveImageIndex(0);
  };

  const handleCardClick = () => {
    navigate(FLAT_PAGE.replace(':id', data.id.toString()));
  };

  let discountedPrice = Number(data.price);
  const areaNum = Number(data.area);
  if (typeof data.discountProperty === 'number' && data.discountProperty > 0 && !isNaN(areaNum) && !isNaN(discountedPrice)) {
    discountedPrice = Number(data.price) - data.discountProperty * areaNum;
    if (discountedPrice < 0) discountedPrice = 0;
  }
  const hasDiscount = typeof data.discountProperty === 'number' && data.discountProperty > 0 && discountedPrice !== Number(data.price);

  const displayPricePerM2 = getDisplayPricePerM2(data.pricePerM2, data.discountProperty);
  const formattedArea = String(data.area).replace('.', ',');

  return (
    <div
      className={styles.wrapper}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onMouseMove={hasSecondImage ? handleMouseMove : undefined}
      onClick={handleCardClick}
      style={{ cursor: 'pointer' }}
    >
      <div className={styles.info}>
        <div className={styles.top}>
          <div className={styles.room}>
            <div className={styles.room_number}>
              <h4>{data.roomType || '-'}</h4>
            </div>
            <div className={styles.room_m2}>
              <h4>
                от <span>{formattedArea}</span> м²
              </h4>
            </div>
            {isSold && (
              <div className={styles.sold_badge}>Продано</div>
            )}
          </div>
          <div className={styles.buttons}>
            <div className={styles.buttons}>
              <CardIconButton 
                iconType='icon2' 
                isActive={isFavorite} 
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  handleToggleFavorite();
                }} 
              />
              <CardIconButton 
                iconType='icon3' 
                isActive={isCompared} 
                onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
                  e.stopPropagation();
                  handleToggleCompare();
                }} 
              />
            </div>
          </div>
        </div>
        <div className={styles.additional_info}>
          <div>
            <p className="text_body_b3">
              <span>{data.entrance ?? '-'} из {data.totalEntrances ?? '-'}</span> подъезд
            </p>
          </div>
          <div className={styles.additional_info_separator}></div>
          <div>
            <p className="text_body_b3">
              <span>{data.floor ?? '-'} из {data.totalFloors ?? '-'}</span> этаж
            </p>
          </div>
        </div>
      </div>
      <div className={styles.image}>
        {primaryImage && (
          <img src={primaryImage} className={activeImageIndex === 0 ? styles.active : ''} />
        )}
        {hasSecondImage && (
          <img
            src={data.imageSecond}
            className={activeImageIndex === 1 ? styles.active : ''}
          />
        )}
        {isHovered && hasSecondImage && (
          <div className={styles.image_indicator}>
            <span className={activeImageIndex === 0 ? styles.active : ''}></span>
            <span className={activeImageIndex === 1 ? styles.active : ''}></span>
          </div>
        )}
      </div>
      <div className={styles.price_block}>
        {!isSold && hasDiscount && (
          <div className={styles.discount}>
            <div className={styles.discount_icon}>
              <Icon name="hotSale" width="13" height="16" />
            </div>
            <div className={styles.discount_title}>
              <p className="text_subheaders_sh3">Выгодное предложение</p>
            </div>
          </div>
        )}
        {isSold ? (
          <div className={styles.price_sold}>
            <h4>Продано</h4>
          </div>
        ) : (
          <div className={styles.price_group}>
            <div className={styles.price_m2}>
              <p className="text_body_b3">
                от <span>{displayPricePerM2}</span> ₽ за м²
              </p>
            </div>
            <div className={styles.price_area}>
              <div className={styles.price}>
                <h4>
                  от <span>{typeof discountedPrice === 'number' && !isNaN(discountedPrice) ? Number(discountedPrice).toLocaleString('ru-RU') : '—'}</span> ₽
                </h4>
              </div>
              {hasDiscount && typeof data.price === 'number' && (
                <div className={styles.price_no_discount}>
                  <p className="text_body_b2">
                    от <span>{Number(data.price).toLocaleString('ru-RU')}</span> ₽
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
